import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAgents } from '../../hooks/useAgents'
import { useClusters, useMachines, useNetworks } from '../../hooks/useInfra'

interface Hit {
  kind: string
  label: string
  detail: string
  to: string
}

// GlobalSearch (Ctrl/Cmd+K): names, IPs, MACs, serials and switch ports across
// machines, clusters, networks and agents.
export function GlobalSearch() {
  const navigate = useNavigate()
  const { data: machines } = useMachines()
  const { data: clusters } = useClusters()
  const { data: networks } = useNetworks()
  const { data: agents } = useAgents()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const ref = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        ref.current?.focus()
        setOpen(true)
      }
    }
    const click = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', key)
    document.addEventListener('mousedown', click)
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('mousedown', click) }
  }, [])

  const hits = useMemo<Hit[]>(() => {
    const n = q.trim().toLowerCase()
    if (n.length < 2) return []
    const out: Hit[] = []
    const has = (...xs: (string | undefined)[]) => xs.some((x) => x && x.toLowerCase().includes(n))
    for (const m of machines || []) {
      const macs = (m.nics || []).map((x) => x.mac_address)
      const ports = Object.values(m.nic_locations || {}).flat()
      const ips = [...(m.addresses || []).map((a) => a.ip), ...(m.host_addresses || []).map((h) => h.address.split('/')[0])]
      if (has(m.name, m.bmc_address, m.role, m.hardware?.text, ...macs, ...ports, ...ips)) {
        const why = [m.bmc_address, ...ips, ...macs, ...ports].find((x) => x && x.toLowerCase().includes(n))
        out.push({ kind: 'Machine', label: m.name, detail: why || `${m.role || m.class} · ${m.state}`, to: `/ops/machines/${m.id}` })
      }
    }
    for (const c of clusters || []) {
      if (has(c.name, c.id, c.name_prefix)) out.push({ kind: 'Cluster', label: c.name, detail: c.type, to: `/ops/clusters/${c.id}` })
    }
    for (const nw of networks || []) {
      if (has(nw.name, nw.cidr, nw.site, nw.vlan ? `vlan ${nw.vlan}` : '')) {
        out.push({ kind: 'Network', label: `${nw.site} / ${nw.name}`, detail: `${nw.cidr}${nw.vlan ? ` · VLAN ${nw.vlan}` : ''}`, to: `/ops/networks/${nw.id}` })
      }
    }
    for (const a of agents || []) {
      if (has(a.hostname, a.ip_address, a.id)) out.push({ kind: 'Agent', label: a.hostname, detail: a.ip_address, to: `/ops/agents?q=${encodeURIComponent(a.hostname)}` })
    }
    return out.slice(0, 12)
  }, [q, machines, clusters, networks, agents])

  const go = (h: Hit) => {
    navigate(h.to)
    setQ('')
    setOpen(false)
    ref.current?.blur()
  }

  return (
    <div className="relative w-52 lg:w-64" ref={box}>
      <input ref={ref} type="search" value={q} placeholder="Search…  ⌘K" title="Name, IP, MAC, switch port" aria-label="Search everything"
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)) }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
          if (e.key === 'Enter' && hits[active]) go(hits[active])
          if (e.key === 'Escape') { setOpen(false); ref.current?.blur() }
        }}
        className="h-8 w-full rounded-md border bg-background px-2.5 text-[13px]" />
      {open && q.trim().length >= 2 && (
        <div className="absolute right-0 z-40 mt-1 max-h-96 w-[28rem] max-w-[90vw] overflow-auto rounded-md border bg-popover py-1 text-sm shadow-lg">
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">No matches</div>
          ) : hits.map((h, i) => (
            <button key={h.kind + h.to} onMouseEnter={() => setActive(i)} onClick={() => go(h)}
              className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left ${i === active ? 'bg-muted' : ''}`}>
              <span className="w-16 shrink-0 text-xs text-muted-foreground">{h.kind}</span>
              <span className="font-medium">{h.label}</span>
              <span className="truncate font-mono text-xs text-muted-foreground">{h.detail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
